import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { AUTH } from '@/constants/testIds';
import { toast } from 'sonner';

export default function Login() {
  const { user, login, signup } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [allowSignup, setAllowSignup] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => { if (user) nav('/', { replace: true }); }, [user, nav]);
  useEffect(() => {
    api.get('/auth/status').then((r) => {
      const claimed = r.data?.claimed;
      setAllowSignup(!claimed);
      setMode(claimed ? 'login' : 'signup');
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (mode === 'forgot') {
        await api.post('/auth/forgot-password', { email });
        setForgotSent(true);
        return;
      }
      if (mode === 'signup') await signup(email, password, name || 'Martyn');
      else await login(email, password);
      toast.success('Welcome to Set The Pace OS');
      nav('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-1 bg-foreground text-white relative overflow-hidden">
        <div className="absolute inset-0 stp-grain opacity-20" />
        <div className="absolute top-0 right-0 w-2/3 h-2/3 bg-primary/10 blur-3xl rounded-full" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src="/stp-icon-192.png" alt="Set The Pace Health & Fitness" className="w-9 h-9 rounded-sm object-contain" />
            <div className="display text-base tracking-tight">Set The Pace OS</div>
          </div>
          <div className="space-y-6 max-w-lg">
            <div className="micro-label text-primary">Business Operating System</div>
            <h1 className="display text-5xl xl:text-6xl leading-[0.95]">
              Every relationship.<br/>Every conversation.<br/>
              <span className="text-primary">In one place.</span>
            </h1>
            <p className="text-zinc-400 leading-relaxed">
              The private command centre for Set The Pace Health &amp; Fitness — built to remember everything so you can focus on the work that matters.
            </p>
          </div>
          <div className="text-xs text-zinc-500">© Set The Pace Health &amp; Fitness</div>
        </div>
      </div>

      {/* Right form */}
      <div
        className="flex-1 flex items-center justify-center p-6 bg-neutral-950"
        style={{ backgroundImage: 'radial-gradient(120% 70% at 50% -10%, rgba(255,215,0,0.10), transparent 55%)' }}
      >
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center lg:hidden" data-testid="coach-login-brand">
            <img src="/stp-icon-192.png" alt="Set The Pace Health & Fitness" className="mx-auto h-24 w-24 rounded-lg object-contain" />
            <h1 className="mt-3 display text-2xl tracking-tight text-neutral-50">STP OS</h1>
          </div>
          <div className="mb-8">
            <div className="micro-label mb-2 text-[#FFD700]">{mode === 'signup' ? 'Claim your account' : (mode === 'forgot' ? 'Reset your password' : 'Welcome back')}</div>
            <h2 className="display text-3xl text-neutral-50">{mode === 'signup' ? 'Set up Set The Pace OS' : (mode === 'forgot' ? 'Forgot password' : 'Sign in')}</h2>
            <p className="text-sm text-neutral-400 mt-1">
              {mode === 'signup' ? 'Create the single owner account.' : (mode === 'forgot' ? (forgotSent ? 'Check your inbox for the secure reset link.' : "Enter your email and we'll send you a reset link.") : 'Use your owner credentials.')}
            </p>
          </div>

          {mode === 'forgot' && forgotSent ? (
            <div className="space-y-4" data-testid="coach-forgot-sent">
              <p className="text-sm text-neutral-400">
                If an account exists for that email, we've sent a link to reset your password. It expires in 60 minutes.
              </p>
              <button type="button" data-testid="coach-forgot-back"
                onClick={() => { setForgotSent(false); setMode('login'); }}
                className="w-full bg-primary text-foreground display tracking-wide text-sm py-2.5 rounded-sm hover:brightness-95 transition-all">
                Back to sign in
              </button>
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-3">
            {mode === 'signup' && (
              <div>
                <label className="micro-label block mb-1 text-neutral-400">Your name</label>
                <input data-testid={AUTH.nameInput} value={name} onChange={(e) => setName(e.target.value)} required
                  className="w-full px-3 py-2.5 bg-neutral-900 border border-neutral-800 rounded-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-[#FFD700] text-sm" />
              </div>
            )}
            <div>
              <label className="micro-label block mb-1 text-neutral-400">Email</label>
              <input data-testid={AUTH.emailInput} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-3 py-2.5 bg-neutral-900 border border-neutral-800 rounded-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-[#FFD700] text-sm" />
            </div>
            {mode !== 'forgot' && (
            <div>
              <label className="micro-label block mb-1 text-neutral-400">Password</label>
              <input data-testid={AUTH.passwordInput} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                className="w-full px-3 py-2.5 bg-neutral-900 border border-neutral-800 rounded-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-[#FFD700] text-sm" />
            </div>
            )}
            {mode === 'forgot' && (
              <p data-testid="coach-forgot-expiry-note" className="text-xs text-neutral-500">
                We'll email you a secure link to reset your password. For your security, the link expires 60 minutes after it's sent.
              </p>
            )}

            <button
              type="submit" disabled={busy}
              data-testid={mode === 'signup' ? AUTH.signupBtn : (mode === 'forgot' ? 'coach-forgot-submit' : AUTH.loginBtn)}
              className="w-full bg-primary text-foreground display tracking-wide text-sm py-2.5 rounded-sm hover:brightness-95 transition-all disabled:opacity-50"
            >
              {busy ? 'Please wait…' : (mode === 'signup' ? 'Create account' : (mode === 'forgot' ? 'Send reset link' : 'Sign in'))}
            </button>

            {mode === 'login' && (
              <button type="button" data-testid="coach-forgot-link" onClick={() => { setForgotSent(false); setMode('forgot'); }}
                className="w-full text-xs text-neutral-400 hover:text-neutral-100 py-2">
                Forgot password?
              </button>
            )}
            {mode === 'forgot' && (
              <button type="button" data-testid="coach-forgot-cancel" onClick={() => setMode('login')}
                className="w-full text-xs text-neutral-400 hover:text-neutral-100 py-2">
                Back to sign in
              </button>
            )}

            {allowSignup && mode !== 'forgot' && (
              <button type="button" data-testid={AUTH.toggleMode} onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                className="w-full text-xs text-neutral-400 hover:text-neutral-100 py-2">
                {mode === 'login' ? 'Need to set up your account?' : 'Already have an account? Sign in'}
              </button>
            )}
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
